/**
 * Team Settings → Success Measure (FR-PLAY-07).
 *
 * The Success Measure decides whether a play "worked", and it drives the
 * Coaches Highlight success rates. The platform ships defaults (run: 4 yards on
 * early downs, pass: 5, chains on late downs); a Team Admin overrides the
 * thresholds and can switch the measure off per phase.
 *
 * Two properties this panel must preserve:
 *   1. **Versioned, never retroactive.** Saving appends a new config version
 *      server-side; plays already logged keep the version that judged them. The
 *      panel states that, because a coach reading a mid-season change otherwise
 *      assumes old plays are re-scored.
 *   2. **Read-only for non-admins.** `can_write` comes from the server; when it
 *      is false the inputs are disabled rather than hidden, so any coach can see
 *      the rules their success rates are based on.
 */
import { useEffect, useState } from 'react';

import { useApiClient } from '../../app/providers';
import { useToast } from '../../app/toast-context';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { DeskSection } from '../../components/desk-section';
import { UnsavedChangesGuard } from '../../components/unsaved-changes-guard';
import { ApiError } from '../../lib/api-error';
import {
  DISTANCE_TO_FIRST_DOWN,
  type SuccessMeasureConfig,
  type SuccessMeasureConfigView,
  type SuccessMeasurePhaseRule,
} from '../../lib/api-types';

import { MAX_YARDS, PhaseCard } from './success-measure-phase-card';

type PhasePath = 'offense.run' | 'offense.pass' | 'defense' | `special_teams.${string}`;

const PHASE_LABELS: Record<string, string> = {
  'offense.run': 'Offense — run',
  'offense.pass': 'Offense — pass',
  defense: 'Defense',
  field_goal: 'Field goal',
  extra_point: 'Extra point',
  punt: 'Punt',
  kick_return: 'Kick return',
  punt_return: 'Punt return',
  kickoff: 'Kickoff',
};

function readRule(config: SuccessMeasureConfig, path: PhasePath): SuccessMeasurePhaseRule {
  if (path === 'offense.run') return config.offense.run;
  if (path === 'offense.pass') return config.offense.pass;
  if (path === 'defense') return config.defense;
  return config.special_teams[path.slice('special_teams.'.length)] ?? { is_enabled: false };
}

function writeRule(
  config: SuccessMeasureConfig,
  path: PhasePath,
  rule: SuccessMeasurePhaseRule
): SuccessMeasureConfig {
  if (path === 'offense.run') return { ...config, offense: { ...config.offense, run: rule } };
  if (path === 'offense.pass') return { ...config, offense: { ...config.offense, pass: rule } };
  if (path === 'defense') return { ...config, defense: rule };
  const key = path.slice('special_teams.'.length);
  return { ...config, special_teams: { ...config.special_teams, [key]: rule } };
}

function validate(config: SuccessMeasureConfig): Record<string, string> {
  const errors: Record<string, string> = {};
  const check = (path: PhasePath, rule: SuccessMeasurePhaseRule): void => {
    for (const key of ['early_down_yards', 'late_down_yards'] as const) {
      const value = rule[key];
      if (value === undefined || value === DISTANCE_TO_FIRST_DOWN) continue;
      if (!Number.isInteger(value) || value < 0 || value > MAX_YARDS) {
        errors[`${path}.${key}`] =
          `Enter a whole number of yards between 0 and ${String(MAX_YARDS)}.`;
      }
    }
    const fallback = rule.late_down_fallback_yards;
    if (
      fallback !== undefined &&
      (!Number.isInteger(fallback) || fallback < 0 || fallback > MAX_YARDS)
    ) {
      errors[`${path}.late_down_fallback_yards`] =
        `Enter a whole number of yards between 0 and ${String(MAX_YARDS)}.`;
    }
  };
  check('offense.run', config.offense.run);
  check('offense.pass', config.offense.pass);
  check('defense', config.defense);
  return errors;
}

export function SuccessMeasureSettings(): JSX.Element {
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const [view, setView] = useState<SuccessMeasureConfigView | null>(null);
  const [draft, setDraft] = useState<SuccessMeasureConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isConfirming, setIsConfirming] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    apiClient
      .getSuccessMeasureConfig(controller.signal)
      .then((loaded) => {
        setView(loaded);
        setDraft(loaded.config);
        setLoadError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(
          ApiError.isApiError(caught)
            ? caught.message
            : 'The Success Measure rules could not be loaded.'
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [apiClient, reloadToken]);

  if (view === null || draft === null) {
    if (isLoading) {
      return (
        <p role="status" className="muted">
          Loading Success Measure rules…
        </p>
      );
    }
    return (
      <div className="banner banner--error" role="alert">
        <p>{loadError ?? 'The Success Measure rules could not be loaded.'}</p>
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={() => setReloadToken((token) => token + 1)}
        >
          Try again
        </button>
      </div>
    );
  }

  const isReadOnly = !view.can_write;

  async function save(): Promise<void> {
    setSaveError(null);
    try {
      const saved = await apiClient.updateSuccessMeasureConfig(draft as SuccessMeasureConfig);
      setView(saved);
      setDraft(saved.config);
      setIsConfirming(false);
      showToast(
        `Success Measure rules saved as version ${String(saved.config.version)}.`,
        'success'
      );
    } catch (caught) {
      if (ApiError.isApiError(caught) && typeof caught.details?.field === 'string') {
        setErrors({ [caught.details.field]: caught.message });
        setIsConfirming(false);
        return;
      }
      setSaveError(
        ApiError.isApiError(caught)
          ? caught.message
          : 'The Success Measure rules could not be saved. Please try again.'
      );
    }
  }

  const specialTeamPaths = Object.keys(draft.special_teams).sort();

  return (
    <div className="team-settings__panel">
      <UnsavedChangesGuard
        isDirty={!isReadOnly && JSON.stringify(draft) !== JSON.stringify(view.config)}
      />
      <DeskSection
        title={
          <span className="team-settings__panel-head">
            Success Measure
            <span className="badge badge--muted">Version {String(draft.version)}</span>
            {view.is_platform_default ? (
              <span className="badge badge--muted">Platform defaults</span>
            ) : null}
          </span>
        }
        description="Whether a play counts as a success on Coaches Highlight and Stat Tracker. Saving creates a new version — plays already logged keep the rules that judged them, so past reports never change."
      >
        {isReadOnly ? (
          <p className="banner banner--info" role="status">
            Only a Team Admin can change these rules. You are seeing the rules your success rates
            are based on.
          </p>
        ) : null}

        <div className="team-settings__phases">
          {(['offense.run', 'offense.pass', 'defense'] as const).map((path) => (
            <PhaseCard
              key={path}
              path={path}
              label={PHASE_LABELS[path] ?? path}
              rule={readRule(draft, path)}
              isDisabled={isReadOnly}
              errors={errors}
              onChange={(rule) => setDraft(writeRule(draft, path, rule))}
            />
          ))}
        </div>
      </DeskSection>

      <DeskSection title="Special teams">
        <div className="team-settings__phases">
          {specialTeamPaths.map((key) => (
            <PhaseCard
              key={key}
              path={`special_teams.${key}`}
              label={PHASE_LABELS[key] ?? key.replace(/_/g, ' ')}
              rule={draft.special_teams[key] ?? { is_enabled: false }}
              isDisabled={isReadOnly}
              errors={errors}
              onChange={(rule) => setDraft(writeRule(draft, `special_teams.${key}`, rule))}
            />
          ))}
        </div>
      </DeskSection>

      {isReadOnly ? null : (
        <div className="desk-form__actions team-settings__save-row">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              const validation = validate(draft);
              setErrors(validation);
              if (Object.keys(validation).length === 0) setIsConfirming(true);
            }}
          >
            Save Success Measure rules
          </button>
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => {
              setDraft(view.platform_defaults);
              setErrors({});
            }}
          >
            Load platform defaults
          </button>
        </div>
      )}

      {isConfirming ? (
        <ConfirmDialog
          title="Save new Success Measure version?"
          consequence={`Version ${String(draft.version + 1)} applies to plays logged from now on. Plays already logged keep version ${String(draft.version)}, so historical success rates do not move.`}
          confirmLabel="Save new version"
          error={saveError}
          onConfirm={() => void save()}
          onCancel={() => {
            setIsConfirming(false);
            setSaveError(null);
          }}
        />
      ) : null}
    </div>
  );
}
